'use client';

import React, { useState } from 'react';

import { formatCountRu } from '@/lib/russian-plural';

export type PriceCheckOption = {
  id: string;
  group: string;
  label: string;
  medianPriceRub: number;
  p25PriceRub: number | null;
  p75PriceRub: number | null;
  snapshotCount: number;
  priceCoveragePercent: number;
};

function formatRub(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

function verdictFor(option: PriceCheckOption, price: number): string {
  const { p25PriceRub: p25, medianPriceRub: median, p75PriceRub: p75 } = option;
  if (p25 !== null && price < p25) {
    return 'Ваша цена ниже P25 — дешевле большинства карточек этой выдачи. Проверьте, не теряете ли маржу без необходимости.';
  }
  if (price <= median) {
    return 'Ваша цена в нижней половине коридора (между P25 и медианой) — конкурентная зона по цене.';
  }
  if (p75 === null || price <= p75) {
    return 'Ваша цена в верхней половине коридора (между медианой и P75). Проверьте, чем подкреплена премия: бренд, рейтинг, доставка или контент.';
  }
  return 'Ваша цена выше P75 — дороже большинства карточек этой выдачи. Премию должны объяснять бренд, рейтинг, комплектация или доставка.';
}

// Расчёт выполняется только в браузере: введённая цена никуда не отправляется и не сохраняется.
export function PriceCheckWidget({ options, collectionDateLabel }: { options: PriceCheckOption[]; collectionDateLabel: string }) {
  const [selectedId, setSelectedId] = useState(options[0]?.id ?? '');
  const [priceInput, setPriceInput] = useState('');

  if (options.length === 0) {
    return null;
  }

  const selected = options.find((option) => option.id === selectedId) ?? options[0];
  const parsedPrice = Number(priceInput.trim().replace(/\s/g, '').replace(',', '.'));
  const hasPrice = priceInput.trim() !== '' && Number.isFinite(parsedPrice) && parsedPrice > 0;
  const groups = [...new Set(options.map((option) => option.group))];

  // Заголовок и вводная фраза принадлежат секции страницы — виджет рендерит только форму и вердикт.
  return (
    <div className="max-w-2xl">
      <div className="grid gap-3 md:grid-cols-[1.4fr_1fr]">
        <select
          className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
          value={selected.id}
          onChange={(event) => setSelectedId(event.target.value)}
          aria-label="Запрос для проверки цены"
        >
          {groups.map((group) => (
            <optgroup key={group} label={group}>
              {options
                .filter((option) => option.group === group)
                .map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
            </optgroup>
          ))}
        </select>
        <input
          type="text"
          inputMode="decimal"
          value={priceInput}
          onChange={(event) => setPriceInput(event.target.value)}
          placeholder="Ваша цена, ₽"
          aria-label="Ваша цена в рублях"
          className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
        />
      </div>
      <div className="mt-3 rounded-2xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs leading-5 text-[var(--site-ink-muted)]">
        <span className="font-semibold text-[var(--site-ink)]">Коридор выдачи: </span>
        P25 {selected.p25PriceRub !== null ? formatRub(selected.p25PriceRub) : '—'}
        {' · медиана '}{formatRub(selected.medianPriceRub)}
        {' · P75 '}{selected.p75PriceRub !== null ? formatRub(selected.p75PriceRub) : '—'}
      </div>
      {hasPrice ? (
        <p className="mt-3 rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2 text-sm leading-6 text-[var(--site-ink)]">
          {verdictFor(selected, parsedPrice)}
        </p>
      ) : null}
      <p className="mt-3 text-xs leading-5 text-[var(--site-ink-muted)]">
        По снимку выдачи за {collectionDateLabel}: {formatCountRu(selected.snapshotCount, ['карточка', 'карточки', 'карточек'])},
        покрытие цен {selected.priceCoveragePercent}%. Ничего не отправляется и не сохраняется.
      </p>
    </div>
  );
}
